import { RefObject } from 'react';
import { PATHS } from '@belong/common';
import { Divider, Text, LinkBase } from '@belong/ui';
import clsx from 'clsx';
import { UnitListingDetailsModel } from 'src/api/models';
import { FavoriteButton } from 'src/features/listing-page/components/favorite-button/favorite-button';
import { getNormalizedFavoritesByHomeId } from 'src/features/listing-page/utils';
import { useResidentFavorites } from 'src/features/residents';
import { ApplyButton } from '../apply-button/apply-button';
import { DepositTabs } from '../deposit-tabs/deposit-tabs';
import { RentalExperienceCta } from '../rental-experience-cta/rental-experience-cta';
import { RentAmount } from '../rent-amount/rent-amount';
import { ScheduleTour } from '../schedule-tour/schedule-tour';
import { UtilitiesSection } from '../utilities-section/utilities-section';
import css from './sidebar.module.css';

type SidebarProps = {
  home: UnitListingDetailsModel;
  applyRef?: RefObject<HTMLAnchorElement>;
  onTourHome: () => void;
};

export function Sidebar({
  home,
  applyRef,
  onTourHome,
}: SidebarProps): JSX.Element {
  const { basicInfo, listingInfo } = home;
  const { unitId } = basicInfo || {};
  const { rentAmount, isDemoUnit, isAvailableForRent, isComingSoon } =
    listingInfo || {};

  // Coming Soon homes have pricing and terms already set and published, even though
  // isAvailableForRent stays false until Full Listing Published — show them like any
  // other published listing rather than hiding rent/deposit info (LOVE-335).
  const shouldShowListingTerms =
    !isDemoUnit && !!(isAvailableForRent || isComingSoon);
  const shouldShowRentAmount = shouldShowListingTerms && !!rentAmount;

  const { favorites } = useResidentFavorites();
  const normalizedFavoritesByHomeId = getNormalizedFavoritesByHomeId(
    favorites || [],
  );

  return (
    <div
      className={clsx(
        'flex flex-col items-center overflow-scroll',
        css.sidebar,
      )}
    >
      {shouldShowRentAmount && (
        <RentAmount rentAmount={rentAmount} priceVariant="h2" />
      )}

      <div className="my-sm w-full">
        <ApplyButton
          homeId={home.basicInfo?.unitId}
          listingId={home.listingInfo?.uniqueId}
          isDemoUnit={home.listingInfo?.isDemoUnit}
          isAvailableForRent={home.listingInfo?.isAvailableForRent}
          isComingSoon={home.listingInfo?.isComingSoon}
          availabilityStatus={home.listingInfo?.availabilityStatus}
          size="fluid"
          ref={applyRef}
        />
      </div>

      <div className="mb-sm w-full">
        <ScheduleTour onClick={onTourHome} home={home} />
      </div>

      <div className="mb-sm">
        <FavoriteButton
          homeId={unitId!}
          // `!`: the sidebar renders inside the house page, whose home always
          // carries `basicInfo.unitId`.
          isFavorite={!!normalizedFavoritesByHomeId[unitId!]}
          key={unitId}
          showLabel
        />
      </div>

      {shouldShowListingTerms && (
        <>
          <div className="mb-xl w-full">
            <DepositTabs home={home} />
          </div>

          {home.utilities?.utilities && (
            <>
              <Divider />
              <div className="w-full  py-md">
                <UtilitiesSection utilities={home.utilities.utilities} />
              </div>
            </>
          )}

          <div className="mb-lg w-full">
            <Divider />
          </div>
        </>
      )}

      <div className="mb-lg flex w-full">
        <div className="mr-md">Questions or concerns?</div>
        <LinkBase href={PATHS.SUPPORT}>
          <Text
            fontWeight="semibold"
            className="uppercase"
          >{`Let's Talk`}</Text>
        </LinkBase>
      </div>

      <RentalExperienceCta />
    </div>
  );
}
